/**
 * Shared authentication utilities for cross-subdomain SSO
 *
 * Uses a cookie on .soliddigital.com domain to share auth state
 * between hub.soliddigital.com, accounts.soliddigital.com, etc.
 */

const SHARED_TOKEN_COOKIE = 'solid_auth_token';
const SHARED_REFRESH_COOKIE = 'solid_refresh_token';

/**
 * Get the root domain for cookies (e.g., ".soliddigital.com")
 * Returns empty string for localhost (cookies work without domain on localhost)
 */
function getCookieDomain(): string {
  const hostname = window.location.hostname;

  // Localhost - no domain needed
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '';
  }

  // IP address - no domain
  if (/^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)) {
    return '';
  }

  // Extract root domain (e.g., "soliddigital.com" from "hub.soliddigital.com")
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    return '.' + parts.slice(-2).join('.');
  }

  return '';
}

/**
 * Set the shared auth token cookie
 */
export function setSharedAuthToken(accessToken: string, refreshToken?: string | null): void {
  const domain = getCookieDomain();
  const isSecure = window.location.protocol === 'https:';
  const sameSite = 'Lax';

  // Access token - short lived, session cookie
  let cookieStr = `${SHARED_TOKEN_COOKIE}=${encodeURIComponent(accessToken)}; path=/; SameSite=${sameSite}`;
  if (domain) {
    cookieStr += `; domain=${domain}`;
  }
  if (isSecure) {
    cookieStr += '; Secure';
  }
  document.cookie = cookieStr;

  // Refresh token - longer lived
  if (refreshToken) {
    let refreshCookieStr = `${SHARED_REFRESH_COOKIE}=${encodeURIComponent(refreshToken)}; path=/; SameSite=${sameSite}; max-age=2592000`; // 30 days
    if (domain) {
      refreshCookieStr += `; domain=${domain}`;
    }
    if (isSecure) {
      refreshCookieStr += '; Secure';
    }
    document.cookie = refreshCookieStr;
  }
}

/**
 * Get the shared auth token from cookie
 */
export function getSharedAuthToken(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === SHARED_TOKEN_COOKIE && value) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

/**
 * Get the shared refresh token from cookie
 */
export function getSharedRefreshToken(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === SHARED_REFRESH_COOKIE && value) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

/**
 * Clear the shared auth cookies (on logout)
 */
export function clearSharedAuthTokens(): void {
  const domain = getCookieDomain();

  // Clear access token
  let clearStr = `${SHARED_TOKEN_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  if (domain) {
    clearStr += `; domain=${domain}`;
  }
  document.cookie = clearStr;

  // Clear refresh token
  let clearRefreshStr = `${SHARED_REFRESH_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  if (domain) {
    clearRefreshStr += `; domain=${domain}`;
  }
  document.cookie = clearRefreshStr;
}

/**
 * Check if a JWT token is expired (with 60s buffer)
 */
export function isTokenExpired(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;

    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const exp = payload.exp * 1000; // Convert to milliseconds

    // Consider expired if within 60 seconds of expiration
    return Date.now() > exp - 60000;
  } catch {
    return true;
  }
}

/**
 * Decode a JWT token payload
 */
export function decodeToken(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payload);
  } catch {
    return null;
  }
}
